<template>
  <div>
    <div v-if="showShell" class="console-shell">
      <aside class="sidebar">
        <div class="brand-block">
          <div class="brand-mark"></div>
          <div class="brand-text">
            <h1>AI网关</h1>
            <small>AI Router Terminal</small>
          </div>
        </div>

        <nav class="side-nav">
          <template v-for="group in groupedMenus">
            <div :key="group.name + '-label'" class="nav-section-label">{{ group.name }}</div>
            <router-link
              v-for="menu in group.items"
              :key="menu.path"
              :to="menu.path"
              :class="{ active: isMenuActive(menu) }"
            >
              <i class="ico" v-html="menuIcon(menu)"></i>
              <span>{{ menu.title }}</span>
            </router-link>
          </template>
        </nav>

        <div class="side-status">
          <div class="pulse-row">
            <span>路由集群在线</span>
            <i class="pulse"></i>
          </div>
          <div class="mini-line">
            <svg viewBox="0 0 180 32" preserveAspectRatio="none">
              <path d="M0 22 C22 18, 28 6, 46 10 S72 30, 92 18 S118 7, 138 11 S160 24, 180 15" fill="none" stroke="#32c8f5" stroke-width="2"/>
            </svg>
          </div>
        </div>
      </aside>

      <div class="main-area">
        <header class="topbar">
          <div class="topbar-title-wrap">
            <h2 class="topbar-title">{{ pageTitle }}</h2>
          </div>

          <div class="topbar-actions">
            <!-- 通知铃铛 -->
            <el-popover
              v-if="!notificationForbidden"
              placement="bottom-end"
              width="420"
              trigger="click"
              popper-class="notification-popover"
              @show="onPopoverShow">
              <div slot="reference" class="notification-bell" :class="{ 'has-unread': unreadCount > 0 }">
                <i class="el-icon-bell"></i>
                <span v-if="unreadCount > 0" class="notification-badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
              </div>
              <div class="notification-popover-body">
                <el-tabs v-model="popoverTab" @tab-click="onPopoverTabChange">
                  <el-tab-pane label="未读消息" name="unread">
                    <div v-loading="unreadLoading" class="all-notification-list">
                      <div v-if="unreadNotifications.length === 0 && !unreadLoading" class="all-notification-empty">暂无未读通知</div>
                      <div
                        v-for="n in unreadNotifications"
                        :key="n.id"
                        class="all-notification-item unread"
                        @click="onUnreadClick(n)">
                        <div class="all-notification-meta">
                          <span class="all-notification-tag" :class="n.type">{{ typeNameMap[n.type] }}</span>
                          <span class="all-notification-time">{{ formatTime(n.createdAt) }}</span>
                        </div>
                        <div class="all-notification-title">{{ n.title }}</div>
                        <div class="all-notification-content">{{ n.content }}</div>
                      </div>
                    </div>
                    <div class="all-notification-footer">
                      <el-pagination
                        layout="total, prev, pager, next"
                        small
                        :total="unreadTotal"
                        :page-size="unreadPageSize"
                        :current-page.sync="unreadPage"
                        @current-change="onUnreadPageChange"
                      />
                    </div>
                  </el-tab-pane>
                  <el-tab-pane label="全部消息" name="all">
                    <div v-loading="allLoading" class="all-notification-list">
                      <div v-if="allNotifications.length === 0 && !allLoading" class="all-notification-empty">暂无通知</div>
                      <div
                        v-for="n in allNotifications"
                        :key="n.id"
                        class="all-notification-item"
                        :class="{ unread: !n.isRead }"
                        @click="onAllNotificationClick(n)">
                        <div class="all-notification-meta">
                          <span class="all-notification-tag" :class="n.type">{{ typeNameMap[n.type] }}</span>
                          <span class="all-notification-time">{{ formatTime(n.createdAt) }}</span>
                        </div>
                        <div class="all-notification-title">{{ n.title }}</div>
                        <div class="all-notification-content">{{ n.content }}</div>
                      </div>
                    </div>
                    <div class="all-notification-footer">
                      <el-pagination
                        layout="total, prev, pager, next"
                        small
                        :total="allTotal"
                        :page-size="allPageSize"
                        :current-page.sync="allPage"
                        @current-change="onAllPageChange"
                      />
                    </div>
                  </el-tab-pane>
                </el-tabs>
              </div>
            </el-popover>
            <el-dropdown @command="handleCommand" trigger="click">
              <button class="topbar-user" type="button">
                <span class="topbar-avatar">{{ avatarText }}</span>
                <span class="topbar-user-meta">
                  <strong>{{ currentUsername }}</strong>
                  <small>已登录</small>
                </span>
              </button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="changePassword"><i class="el-icon-lock"></i> 修改密码</el-dropdown-item>
                <el-dropdown-item command="logout"><i class="el-icon-switch-button"></i> 退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </header>

        <el-dialog title="修改密码" :visible.sync="passwordDialogVisible" width="420px" :close-on-click-modal="false" top="22vh">
          <el-form ref="passwordForm" :model="passwordForm" :rules="passwordRules" label-width="90px" size="small">
            <el-form-item label="原密码" prop="oldPassword">
              <el-input v-model="passwordForm.oldPassword" type="password" autocomplete="off" show-password />
            </el-form-item>
            <el-form-item label="新密码" prop="newPassword">
              <el-input v-model="passwordForm.newPassword" type="password" autocomplete="off" show-password />
            </el-form-item>
            <el-form-item label="确认密码" prop="confirmPassword">
              <el-input v-model="passwordForm.confirmPassword" type="password" autocomplete="off" show-password />
            </el-form-item>
          </el-form>
          <span slot="footer">
            <el-button size="small" @click="passwordDialogVisible = false">取消</el-button>
            <el-button size="small" type="primary" :loading="passwordSubmitting" @click="submitPassword">确定</el-button>
          </span>
        </el-dialog>

        <el-dialog title="通知详情" :visible.sync="notificationDetailVisible" width="460px" :close-on-click-modal="true" top="15vh">
          <div v-if="currentNotification" class="notification-detail">
            <div class="notification-detail-meta">
              <span class="all-notification-tag" :class="currentNotification.type">{{ typeNameMap[currentNotification.type] }}</span>
              <span class="all-notification-time">{{ formatTime(currentNotification.createdAt) }} · {{ formatDateTime(currentNotification.createdAt) }}</span>
            </div>
            <div class="notification-detail-title">{{ currentNotification.title }}</div>
            <div class="notification-detail-content">{{ currentNotification.content }}</div>
          </div>
        </el-dialog>

        <section class="content">
          <div v-if="!tickerForbidden" class="ticker">
            <div class="ticker-title">实时用量</div>
            <div class="ticker-window">
              <div class="ticker-track" :class="{ playing: tickerReady }">
                <template v-for="(item, i) in tickerItems">
                  <span :key="i"><b>{{ item.label }}</b> {{ item.value }} <em :class="item.cls">{{ item.change }}</em></span>
                </template>
                <template v-for="(item, i) in tickerItems">
                  <span :key="'dup-' + i"><b>{{ item.label }}</b> {{ item.value }} <em :class="item.cls">{{ item.change }}</em></span>
                </template>
              </div>
            </div>
          </div>
          <router-view />
        </section>
      </div>
    </div>
    <router-view v-else />
  </div>
</template>

<script>
const GROUP_BY_PATH = [
  { name: '实时总览', patterns: ['/dashboard', '/monitoring-center', '/db-connection', '/tpm-monitor'] },
  { name: '调用治理', patterns: ['/call-logs', '/error-logs', '/security-rules', '/route-strategy', '/ip-whitelist'] },
  { name: 'AI 资产联动', patterns: ['/mcp-servers', '/asset-dashboard'] },
  { name: '客户与账号', patterns: ['/customers', '/customer-models/', '/accounts', '/opc-accounts', '/recharge-logs', '/profile', '/quick-config', '/bills', '/notification'] },
  { name: '模型与价格', patterns: ['/model-catalog', '/suppliers', '/supplier-models/', '/supplier-api-keys/', '/model-prices', '/price-history'] },
  { name: '账单总览', patterns: ['/bills-overview'] }
]

function groupOf(path) {
  for (const g of GROUP_BY_PATH) {
    if (g.patterns.some(p => path === p || path.startsWith(p))) return g.name
  }
  return '其它'
}

export default {
  name: 'App',
  data() {
    const validateConfirmPassword = (rule, value, callback) => {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的新密码不一致'))
        return
      }
      callback()
    }
    return {
      menus: [],
      iconMap: {
        '/dashboard': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M4 7.5 12 3l8 4.5-8 4.5z"/><path d="M4 7.5v9L12 21l8-4.5v-9"/><path d="M12 12v9"/></svg>',
        '/monitoring-center': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M4 19V5"/><path d="M4 19h16"/><path d="M8 16v-5"/><path d="M12 16V8"/><path d="M16 16v-8"/></svg>',
        '/db-connection': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5"/><path d="M3 12c0 1.66 4.03 3 9 3s9-1.34 9-3"/></svg>',
        '/tpm-monitor': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z"/><path d="M12 12l4-4"/><path d="M12 3v3"/><path d="M12 18v-6"/><path d="M3 12h3"/><path d="M18 12h3"/></svg>',
        '/call-logs': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M8 6h12M8 12h12M8 18h12"/><path d="M4 6h.01M4 12h.01M4 18h.01"/></svg>',
        '/error-logs': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 4v10"/><path d="M12 19h.01"/><path d="M4 21h16L12 3z"/></svg>',
        '/security-rules': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>',
        '/route-strategy': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>',
        '/mcp-servers': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>',
        '/asset-analytics': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M3 3v18h18"/><path d="M7 16l4-8 4 4 4-6"/></svg>',
        '/asset-dashboard': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>',
        '/customers': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3"/></svg>',
        '/accounts': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="8" r="3.2"/><path d="M5 20c1.5-4 12.5-4 14 0"/></svg>',
        '/opc-accounts': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="3" y="6" width="18" height="12" rx="2"/><path d="M3 10h18"/><path d="M8 14h3"/></svg>',
        '/profile': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="8" r="3"/><path d="M7 21h10a2 2 0 0 0 2-2c0-3-14-3-14 0a2 2 0 0 0 2 2z"/></svg>',
        '/model-catalog': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="m12 3 9 5-9 5-9-5z"/><path d="m3 13 9 5 9-5"/><path d="m3 17 9 5 9-5"/></svg>',
        '/suppliers': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="6" rx="2"/><rect x="4" y="13" width="16" height="6" rx="2"/><path d="M8 8h.01M8 16h.01"/></svg>',
        '/supplier-models/': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="6" rx="2"/><rect x="4" y="13" width="16" height="6" rx="2"/><path d="M8 8h.01M8 16h.01"/></svg>',
        '/supplier-api-keys/': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="4" y="5" width="16" height="6" rx="2"/><rect x="4" y="13" width="16" height="6" rx="2"/><path d="M8 8h.01M8 16h.01"/></svg>',
        '/model-prices': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M12 3l1.7 5.2L19 10l-5.3 1.8L12 17l-1.7-5.2L5 10l5.3-1.8z"/><path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8z"/></svg>',
        '/price-history': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8"/><path d="M12 7v5l4 2"/></svg>',
        '/quick-config': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>',
        '/customer-models/': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="m12 3 9 5-9 5-9-5z"/><path d="m3 13 9 5 9-5"/><path d="m3 17 9 5 9-5"/></svg>',
        '/bills-overview': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg>',
        '/bills': '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>'
      },
      currentUserName: localStorage.getItem('username') || '管理员',
      passwordDialogVisible: false,
      passwordSubmitting: false,
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {
        oldPassword: [
          { required: true, message: '请输入原密码', trigger: 'blur' }
        ],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '新密码至少 6 位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认新密码', trigger: 'blur' },
          { validator: validateConfirmPassword, trigger: 'blur' }
        ]
      },
      tickerItems: [],
      tickerTimer: null,
      tickerForbidden: false,
      tickerReady: false,
      unreadCount: 0,
      unreadTimer: null,
      notificationForbidden: false,
      popoverTab: 'unread',
      unreadNotifications: [],
      unreadTotal: 0,
      unreadPage: 1,
      unreadPageSize: 10,
      unreadLoading: false,
      allNotifications: [],
      allLoading: false,
      allTotal: 0,
      allPage: 1,
      allPageSize: 10,
      typeNameMap: {
        system: '系统通知',
        billing: '账单提醒',
        activity: '活动公告'
      },
      notificationDetailVisible: false,
      currentNotification: null
    }
  },
  computed: {
    showShell() {
      return this.$route.path !== '/login' && this.$route.path !== '/activate' && this.$route.path !== '/activate-personal'
    },
    pageTitle() {
      if (this.$route.path.startsWith('/supplier-models/')) {
        return (this.$route.query.name || '供应商') + ' - 模型维护'
      }
      if (this.$route.path.startsWith('/supplier-api-keys/')) {
        return (this.$route.query.name || '供应商') + ' - API Key 维护'
      }
      if (this.$route.path.startsWith('/customer-models/')) {
        return (this.$route.query.name || '客户') + ' - 模型价格'
      }
      const menu = this.menus.find(item => item.path === this.$route.path)
      return menu ? menu.title : 'AI 网关'
    },
    groupedMenus() {
      const order = GROUP_BY_PATH.map(g => g.name).concat(['其它'])
      const map = new Map()
      this.menus.forEach(m => {
        const g = groupOf(m.path)
        if (!map.has(g)) map.set(g, [])
        map.get(g).push(m)
      })
      return order
        .filter(name => map.has(name))
        .map(name => ({ name, items: map.get(name) }))
    },
    currentUsername() {
      return this.currentUserName
    },
    avatarText() {
      const name = this.currentUsername
      return name.length >= 2 ? name.substring(0, 2).toUpperCase() : name.toUpperCase()
    }
  },
  watch: {
    showShell: {
      immediate: true,
      handler(value) {
        if (value) {
          this.loadMenus()
          this.tickerItems = []
          this.tickerForbidden = false
          this.tickerReady = false
          clearInterval(this.tickerTimer)
          this.tickerTimer = null
          this.startTicker()
          this.unreadCount = 0
          this.notifications = []
          this.notificationForbidden = false
          clearInterval(this.unreadTimer)
          this.unreadTimer = null
          this.startUnreadPolling()
        } else {
          this.stopUnreadPolling()
        }
      }
    }
  },
  created() {
    this.$root.$on('user-changed', this.updateCurrentUserName)
  },
  beforeDestroy() {
    this.$root.$off('user-changed', this.updateCurrentUserName)
    clearInterval(this.tickerTimer)
    this.stopUnreadPolling()
  },
  methods: {
    async loadMenus() {
      if (!localStorage.getItem('token')) {
        this.menus = []
        return
      }
      try {
        this.menus = await this.$http.get('/admin/menus')
      } catch {
        this.menus = []
      }
    },
    menuIcon(menu) {
      for (const [path, svg] of Object.entries(this.iconMap)) {
        if (menu.path === path || menu.path.startsWith(path)) return svg
      }
      return '<svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path d="M4 7.5 12 3l8 4.5-8 4.5z"/><path d="M4 7.5v9L12 21l8-4.5v-9"/><path d="M12 12v9"/></svg>'
    },
    isMenuActive(menu) {
      if (!menu.activePatterns || menu.activePatterns.length === 0) {
        return this.$route.path === menu.path
      }
      return menu.activePatterns.some(pattern => {
        if (pattern.endsWith('/')) {
          return this.$route.path.startsWith(pattern)
        }
        return this.$route.path === pattern
      })
    },
    handleCommand(command) {
      if (command === 'changePassword') {
        this.openPasswordDialog()
        return
      }
      if (command === 'logout') {
        this.logout('已退出登录')
      }
    },
    updateCurrentUserName(username) {
      this.currentUserName = username || localStorage.getItem('username') || '管理员'
    },
    openPasswordDialog() {
      this.passwordForm = {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      }
      this.passwordDialogVisible = true
      this.$nextTick(() => this.$refs.passwordForm?.clearValidate())
    },
    submitPassword() {
      this.$refs.passwordForm.validate(async valid => {
        if (!valid) return
        this.passwordSubmitting = true
        try {
          await this.$http.post('/admin/change-password', {
            oldPassword: this.passwordForm.oldPassword,
            newPassword: this.passwordForm.newPassword
          })
          this.passwordDialogVisible = false
          this.logout('密码修改成功，请重新登录')
        } catch (e) {
          const msg = e.response?.data?.error
          this.$message.error(msg || '修改密码失败')
        } finally {
          this.passwordSubmitting = false
        }
      })
    },
    logout(message) {
      localStorage.removeItem('token')
      localStorage.removeItem('username')
      this.currentUserName = '管理员'
      this.menus = []
      this.$router.push('/login')
      this.$message.success(message)
    },
    async fetchTicker() {
      try {
        const res = await this.$http.get('/admin/monitoring/realtime')
        if (res.items && res.items.length) {
          this.tickerItems = res.items
          this.tickerReady = true
          return
        }
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.tickerForbidden = true
          clearInterval(this.tickerTimer)
          this.tickerTimer = null
          return
        }
      }
      this.tickerItems = [
        { label: 'Agent资产', value: '18.7亿 token', change: '+16.4%', cls: 'up' },
        { label: 'deepseek-v4-flash', value: '73.71M token', change: '+18.4%', cls: 'up' },
        { label: 'GLM-5.2', value: 'P95 5460ms', change: '-9.3%', cls: 'down' },
        { label: '缓存命中', value: '37.8%', change: '+6.2%', cls: 'up' },
        { label: 'qwen3-max', value: '¥0.004 / 1K', change: '价格变更', cls: 'warn' },
        { label: '错误率', value: '0.28%', change: '-0.04%', cls: 'down' }
      ]
      this.tickerReady = true
    },
    startTicker() {
      if (this.tickerForbidden) return
      this.fetchTicker()
      this.tickerTimer = setInterval(() => this.fetchTicker(), 300000)
    },
    // ========== 通知相关 ==========
    async fetchUnreadCount() {
      try {
        const res = await this.$http.get('/admin/notifications/unread-count')
        this.unreadCount = res.count || 0
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.notificationForbidden = true
          this.stopUnreadPolling()
        }
      }
    },
    async markAsRead(id) {
      try {
        await this.$http.put('/admin/notifications/' + id + '/read')
        this.fetchUnreadCount()
      } catch (e) {
        // 静默失败
      }
    },
    onPopoverShow() {
      this.popoverTab = 'unread'
      this.unreadPage = 1
      this.fetchUnreadNotifications()
    },
    onPopoverTabChange(tab) {
      if (tab.name === 'all') {
        this.allPage = 1
        this.fetchAllNotifications()
      } else {
        this.unreadPage = 1
        this.fetchUnreadNotifications()
      }
    },
    async fetchUnreadNotifications() {
      this.unreadLoading = true
      try {
        const res = await this.$http.get('/admin/notifications/my/unread', {
          params: { page: this.unreadPage, pageSize: this.unreadPageSize }
        })
        this.unreadNotifications = res.records || []
        this.unreadTotal = Number(res.total) || 0
      } catch (e) {
        this.unreadNotifications = []
      } finally {
        this.unreadLoading = false
      }
    },
    async fetchAllNotifications() {
      this.allLoading = true
      try {
        const res = await this.$http.get('/admin/notifications/my', {
          params: { page: this.allPage, pageSize: this.allPageSize }
        })
        this.allNotifications = res.records || []
        this.allTotal = Number(res.total) || 0
      } catch (e) {
        this.allNotifications = []
      } finally {
        this.allLoading = false
      }
    },
    openNotificationDetail(n) {
      this.currentNotification = n
      this.notificationDetailVisible = true
    },
    async onUnreadClick(n) {
      await this.markAsRead(n.id)
      this.unreadNotifications = this.unreadNotifications.filter(x => x.id !== n.id)
      this.unreadTotal = Math.max(0, this.unreadTotal - 1)
      this.openNotificationDetail(n)
    },
    async onAllNotificationClick(n) {
      if (!n.isRead) {
        await this.markAsRead(n.id)
        n.isRead = true
      }
      this.openNotificationDetail(n)
    },
    onAllPageChange(page) {
      this.allPage = page
      this.fetchAllNotifications()
    },
    onUnreadPageChange(page) {
      this.unreadPage = page
      this.fetchUnreadNotifications()
    },
    formatTime(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const now = new Date()
      const diff = now - date
      const minutes = Math.floor(diff / 60000)
      const hours = Math.floor(diff / 3600000)
      const days = Math.floor(diff / 86400000)
      if (minutes < 1) return '刚刚'
      if (minutes < 60) return minutes + ' 分钟前'
      if (hours < 24) return hours + ' 小时前'
      if (days < 30) return days + ' 天前'
      return date.toLocaleDateString()
    },
    formatDateTime(dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const pad = v => String(v).padStart(2, '0')
      return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes())
    },
    startUnreadPolling() {
      this.fetchUnreadCount()
      this.unreadTimer = setInterval(() => this.fetchUnreadCount(), 300000) // 每5分钟轮询
    },
    stopUnreadPolling() {
      clearInterval(this.unreadTimer)
    }
  }
}
</script>

<style scoped>
.console-shell {
  display: grid;
  grid-template-columns: 248px 1fr;
  height: 100vh;
  color: var(--text);
}

/* ---------- Sidebar ---------- */
.sidebar {
  display: grid;
  grid-template-rows: auto 1fr auto;
  height: 100vh;
  padding: 24px 16px 16px;
  border-right: 1px solid var(--line);
  background: rgba(13, 21, 34, 0.9);
  backdrop-filter: blur(20px);
  overflow: hidden;
}

.brand-block {
  display: flex;
  align-items: center;
  gap: 12px;
  height: 52px;
  margin-bottom: 22px;
  padding: 0 6px;
}

.brand-mark {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  display: grid;
  place-items: center;
  flex: none;
  background:
    radial-gradient(circle at 28% 18%, rgba(255,255,255,.94) 0 3%, transparent 4%),
    radial-gradient(circle at 76% 74%, rgba(37,240,255,.42), transparent 42%),
    radial-gradient(circle at 18% 78%, rgba(118,126,255,.28), transparent 36%),
    linear-gradient(150deg, rgba(255,255,255,.18), transparent 34%),
    linear-gradient(145deg, #10203a 0%, #12678b 48%, #20d6f4 100%);
  border: 1px solid rgba(183, 239, 255, .46);
  box-shadow: 0 18px 40px rgba(35, 215, 255, 0.3), 0 0 30px rgba(126,236,255,.18), inset -10px -12px 18px rgba(3,8,18,.5), inset 8px 8px 16px rgba(255,255,255,.16);
  overflow: hidden;
  position: relative;
}

.brand-mark::before {
  content: "";
  position: absolute;
  width: 23px;
  height: 23px;
  clip-path: polygon(50% 0%, 88% 22%, 88% 78%, 50% 100%, 12% 78%, 12% 22%);
  background:
    linear-gradient(135deg, rgba(255,255,255,.98), rgba(205,252,255,.8) 30%, rgba(35,215,255,.35) 31%, rgba(94,118,255,.58) 100%);
  box-shadow: 0 10px 22px rgba(0,0,0,.38), 0 0 22px rgba(66,236,255,.58), inset 0 0 0 1px rgba(255,255,255,.76), inset -5px -6px 10px rgba(5,12,25,.28);
  z-index: 2;
}

.brand-mark::after {
  content: "";
  position: absolute;
  inset: 3px;
  background: no-repeat center/100% 100% url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 72 72' fill='none'%3E%3Cpath d='M5 43C18 26 54 23 67 11' stroke='%23ffe7a6' stroke-width='3.2' stroke-linecap='round' opacity='.98'/%3E%3Cpath d='M5 44C18 60 53 56 67 38' stroke='%2325f0ff' stroke-width='3.6' stroke-linecap='round' opacity='1'/%3E%3Cpath d='M18 18C31 8 50 12 58 27C47 22 29 23 18 35' stroke='%2398f7ff' stroke-width='2.2' stroke-linecap='round' opacity='.72'/%3E%3Ccircle cx='7' cy='43' r='4.1' fill='%23ffffff'/%3E%3Ccircle cx='67' cy='11' r='3.7' fill='%23ffe7a6'/%3E%3Ccircle cx='67' cy='38' r='4.1' fill='%2325f0ff'/%3E%3Ccircle cx='36' cy='36' r='12' stroke='%23ffffff' stroke-width='2.4' opacity='.82'/%3E%3C/svg%3E");
  filter: drop-shadow(0 0 11px rgba(37,240,255,.56));
  opacity: .98;
  z-index: 1;
}

.brand-text h1 {
  margin: 0;
  font-size: 22px;
  line-height: 1;
  font-weight: 850;
  color: #fff;
}

.brand-text small {
  display: block;
  margin-top: 6px;
  color: var(--muted);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* ---------- Nav ---------- */
.side-nav {
  display: grid;
  align-content: start;
  gap: 6px;
  overflow-y: auto;
  padding-right: 4px;
  min-height: 0;
}

.nav-section-label {
  margin: 14px 10px 6px;
  color: #60738f;
  font-size: 11px;
  font-weight: 850;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}
.nav-section-label:first-child {
  margin-top: 0;
}

.side-nav a {
  display: grid;
  grid-template-columns: 26px 1fr;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 42px;
  padding: 0 12px;
  border-radius: 8px;
  color: #a8b7cd;
  font-size: 14px;
  font-weight: 760;
  text-align: left;
}

.ico {
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  border-radius: 6px;
  color: inherit;
}

.ico svg {
  width: 18px;
  height: 18px;
  stroke: currentColor;
  stroke-width: 2;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.side-nav a:hover {
  background: rgba(255, 255, 255, 0.055);
  color: var(--text);
}

.side-nav a.active,
.side-nav a.router-link-exact-active {
  color: #fff;
  background: linear-gradient(90deg, rgba(79, 134, 255, 0.22), rgba(50, 200, 245, 0.08));
  box-shadow: inset 3px 0 0 var(--cyan);
}

/* ---------- Side status ---------- */
.side-status {
  margin-top: 14px;
  padding: 14px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.045);
}

.pulse-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
  font-size: 12px;
  color: var(--muted);
  font-weight: 800;
}

.pulse {
  width: 8px;
  height: 8px;
  border-radius: 99px;
  background: var(--green);
  box-shadow: 0 0 12px rgba(52, 201, 143, 0.55);
}

.mini-line {
  height: 32px;
  background: linear-gradient(90deg, transparent, rgba(50, 200, 245, 0.1));
  position: relative;
  overflow: hidden;
}

.mini-line svg {
  width: 100%;
  height: 100%;
}

/* ---------- Ticker ---------- */
.ticker {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr);
  align-items: center;
  gap: 12px;
  height: 46px;
  padding: 0 14px;
  margin: 0 24px 16px;
  border: 1px solid rgba(50, 200, 245, 0.2);
  border-radius: 8px;
  background: linear-gradient(90deg, rgba(50, 200, 245, 0.09), rgba(79, 134, 255, 0.08));
  overflow: hidden;
}

.ticker-title {
  position: relative;
  z-index: 2;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--cyan);
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
}

.ticker-window {
  min-width: 0;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(90deg, transparent 0, transparent 42px, #000 82px, #000 calc(100% - 28px), transparent 100%);
  mask-image: linear-gradient(90deg, transparent 0, transparent 42px, #000 82px, #000 calc(100% - 28px), transparent 100%);
}

.ticker-track {
  min-width: max-content;
  display: flex;
  align-items: center;
  gap: 34px;
  white-space: nowrap;
  color: #c7d6ec;
  font-size: 12px;
  position: relative;
  z-index: 1;
}
.ticker-track.playing {
  animation: ticker-scroll 28s linear infinite;
}

.ticker-track span {
  display: inline-flex;
  align-items: baseline;
  gap: 5px;
}

.ticker-track b {
  color: #fff;
}

.ticker-track em.up { color: var(--red); font-style: normal; }
.ticker-track em.down { color: var(--green); font-style: normal; }
.ticker-track em.warn { color: var(--amber); font-style: normal; }

@keyframes ticker-scroll {
  from { transform: translateX(0); }
  to { transform: translateX(-50%); }
}

/* ---------- Main area ---------- */
.main-area {
  min-width: 0;
  height: 100vh;
  overflow: auto;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 10;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 28px;
  border-bottom: 1px solid var(--line);
  background: rgba(15, 23, 36, 0.86);
  backdrop-filter: blur(20px);
}

.topbar-title-wrap {
  min-width: 0;
}

.topbar-title {
  margin: 0;
  font-size: 24px;
  font-weight: 860;
  color: var(--text);
  line-height: 1.1;
}

.topbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.topbar-user {
  height: 42px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.055);
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: var(--text);
  cursor: pointer;
}

.topbar-user:hover {
  border-color: rgba(50, 200, 245, 0.42);
  background: rgba(50, 200, 245, 0.08);
}

.topbar-avatar {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--blue), var(--cyan));
  color: #fff;
  font-size: 12px;
  font-weight: 900;
}

.topbar-user-meta {
  display: grid;
  gap: 1px;
  text-align: left;
}

.topbar-user-meta strong {
  font-size: 13px;
  line-height: 1.1;
  color: var(--text);
}

.topbar-user-meta small {
  font-size: 11px;
  line-height: 1.1;
  color: var(--green);
}

.content {
  padding: 22px 28px 42px;
  flex: 1;
  min-height: 0;
}

@media (max-width: 1200px) {
  .console-shell {
    grid-template-columns: 84px 1fr;
  }
  .brand-block {
    justify-content: center;
    padding: 0;
  }
  .brand-text {
    display: none;
  }
  .side-nav a {
    grid-template-columns: 1fr;
    justify-items: center;
    padding: 0;
  }
  .side-nav a .ico {
    font-size: 20px;
  }
  .side-nav a span {
    display: none;
  }
  .nav-section-label {
    display: none;
  }
  .side-status {
    display: none;
  }
}

@media (max-width: 760px) {
  .console-shell {
    display: block;
    height: auto;
    min-height: 100vh;
  }
  .sidebar {
    position: sticky;
    top: 0;
    z-index: 20;
    display: block;
    height: auto;
    padding: 10px 10px 8px;
    border-right: 0;
    border-bottom: 1px solid var(--line);
    overflow: hidden;
  }
  .brand-block {
    height: 38px;
    margin-bottom: 8px;
    justify-content: flex-start;
  }
  .brand-mark {
    width: 32px;
    height: 32px;
  }
  .brand-text {
    display: block;
  }
  .brand-text h1 {
    font-size: 18px;
  }
  .brand-text small {
    display: none;
  }
  .side-nav {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    gap: 10px;
    padding: 0 2px 8px;
    scroll-snap-type: x proximity;
  }
  .side-nav a {
    flex: 0 0 auto;
    width: 58px;
    height: 52px;
    padding: 0;
    grid-template-columns: 1fr;
    align-items: center;
    justify-items: center;
    scroll-snap-align: start;
    border-radius: 10px;
  }
  .side-nav a .ico {
    font-size: 22px;
  }
  .side-nav a .ico svg {
    width: 24px;
    height: 24px;
  }
  .side-status {
    display: none;
  }
  .topbar {
    position: static;
    height: auto;
    min-height: 64px;
    padding: 12px 14px;
    gap: 12px;
    align-items: flex-start;
  }
  .topbar-title {
    font-size: 18px;
  }
  .topbar-actions {
    margin-left: auto;
  }
  .topbar-user {
    height: 38px;
    padding: 0 8px;
  }
  .topbar-user-meta strong {
    font-size: 12px;
  }
  .content {
    padding: 12px 12px 28px;
  }
  .ticker {
    grid-template-columns: 96px minmax(0, 1fr);
    height: 46px;
    padding: 0 14px;
    margin: 0 12px 12px;
  }
  .ticker-title {
    display: flex;
    background: none;
  }
  .ticker-window {
    -webkit-mask-image: none;
    mask-image: none;
  }
  .ticker-track {
    gap: 24px;
    font-size: 12px;
  }
  .ticker-track.playing {
    animation-duration: 12s;
  }
}

/* ---------- Notification Bell ---------- */
.notification-bell {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: var(--radius);
  cursor: pointer;
  color: var(--muted);
  font-size: 18px;
  transition: all 0.2s;
}

.notification-bell:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}

.notification-bell.has-unread {
  color: var(--cyan);
}

.notification-badge {
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 8px;
  background: var(--red);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  text-align: center;
}
</style>

<!-- 通知弹窗覆盖 -->
<style>
.notification-popover.el-popover {
  padding: 20px !important;
  background: #1a2638 !important;
  border: 1px solid var(--line) !important;
  border-radius: var(--radius) !important;
  box-shadow: var(--shadow) !important;
}
.notification-popover .popper__arrow::after {
  border-bottom-color: #1a2638 !important;
}
.notification-popover .el-tabs__header {
  margin: 0;
}
.notification-popover .el-tabs__nav-wrap::after {
  background: var(--line);
}
.notification-popover .el-tabs__item {
  height: 44px;
  line-height: 44px;
  font-size: 14px;
  font-weight: 600;
  color: var(--muted);
  opacity: 1;
}
.notification-popover .el-tabs__item:hover {
  color: #c6d4e9;
}
.notification-popover .el-tabs__item.is-active {
  color: var(--cyan);
}
.notification-popover .el-tabs__active-bar {
  background-color: var(--cyan);
}
.notification-popover .all-notification-list {
  max-height: 360px;
  overflow-y: auto;
  padding: 4px 0;
}
.notification-popover .all-notification-footer {
  padding: 10px 0 0;
  display: flex;
  justify-content: center;
}

/* ---------- 通知列表样式 ---------- */
.all-notification-list {
  min-height: 80px;
}
.all-notification-empty {
  text-align: center;
  padding: 40px 0;
  color: var(--muted);
  font-size: 13px;
}
.all-notification-item {
  position: relative;
  padding: 12px 16px 12px 32px;
  border-bottom: 1px solid var(--line);
  cursor: pointer;
  transition: background 0.15s;
}
.all-notification-item:hover {
  background: rgba(255, 255, 255, 0.03);
}
.all-notification-item::before {
  content: '';
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.2s;
}
.all-notification-item.unread::before {
  background: var(--cyan);
  box-shadow: 0 0 8px rgba(50, 200, 245, 0.7);
}
.all-notification-item.unread {
  background: rgba(37, 240, 255, 0.03);
}
.all-notification-item:not(.unread) {
  opacity: 0.65;
}
.all-notification-item:not(.unread):hover {
  opacity: 1;
}
.all-notification-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
}
.all-notification-tag {
  font-size: 11px;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 4px;
  background: rgba(79, 134, 255, 0.14);
  color: #9fc0ff;
}
.all-notification-tag.billing {
  background: rgba(255, 176, 32, 0.13);
  color: #ffd38a;
}
.all-notification-tag.activity {
  background: rgba(22, 217, 152, 0.11);
  color: #55f0b3;
}
.all-notification-time {
  font-size: 12px;
  color: var(--muted-2);
}
.all-notification-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 2px;
  line-height: 1.4;
}
.all-notification-item:not(.unread) .all-notification-title {
  font-weight: 500;
}
.all-notification-content {
  font-size: 12px;
  color: var(--muted);
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* ---------- 通知详情弹窗 ---------- */
.notification-detail-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.notification-detail-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
  margin-bottom: 10px;
  line-height: 1.5;
}
.notification-detail-content {
  font-size: 13px;
  color: var(--text);
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 320px;
  overflow-y: auto;
  padding: 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--line);
  border-radius: 6px;
}
</style>
